import { Component, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { AllocBatchRerunService } from './alloc-batch-rerun.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-alloc-batch-rerun',
    templateUrl: './alloc-batch-rerun.component.html',
    styleUrls: ['./alloc-batch-rerun.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AllocBatchRerunComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };

  rowData: any = {};

  gridData: any[] = [];

  filterParams: any = {};

  baseURLs: GtwElementsBaseUrlsModel = {} as GtwElementsBaseUrlsModel;
  globalService: any;
  GENERAL_CONFIG: any;
  workspaceData: any[] = [];
  
  userSettings: any = this.appService.USER_SETTINGS;
  logged_in_user: string = '';
  
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  tax_year: string = '';
  scenario: string = '';
  jcd_key: string = '';
  currentyear: string = '';
  modalName: string = '';
  syncSaveDtls: any[] = [];
  allocationLimit: number = 0;
  gridDataMultipleSelect: any[] = [];
  
  title: string = "Confirm";
  
  loading: boolean = false;
  processing: boolean = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private allocBatchRerunService: AllocBatchRerunService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.workspaceData = (header as any).workspaceData ?? this.globalService.workspaceData ?? [];
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(gridPayload) ? gridPayload : gridPayload ? [gridPayload] : [];
    this.globalService.filterParams = this.filterParams;
    this.processGridData();
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }
      
      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      this.setupFilterParams();
      
      this.fetchMaxCountEntities();
    } catch (e) {
      console.error('Error in AllocBatchRerun init:', e);
    }
  }

  private setupFilterParams(): void {
    this.tax_year = this.filterParams.tax_year || '';
    this.scenario = this.filterParams.scenario || '';
    this.jcd_key = this.globalService?.globalParams?.jcd_key || '';
    this.currentyear = this.filterParams.tax_year || '';
  }

  private processGridData(): void {
    this.syncSaveDtls = [];
    
    if (this.gridData && this.gridData.length > 0) {
      this.gridDataMultipleSelect = _.clone(this.gridData);
      
      if (this.gridDataMultipleSelect) {
        for (let key in this.gridDataMultipleSelect) {
          let gridObj: any = {};
          gridObj["combination_key"] = this.gridDataMultipleSelect[key].data?.COMBINATION_KEY || 
                                      this.gridDataMultipleSelect[key].COMBINATION_KEY;
          this.syncSaveDtls.push(gridObj);
        }
      }
    }
  }

  private fetchMaxCountEntities(): void {
    const params = { 
      "action_code": 'mysfkb', 
      "screen_key": this.globalService?.globalParams?.screen_key || ''
    };
    
    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService, this.filterParams, null).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.allocationLimit = parseInt(data.jsonObject[0]?.ATTRIB_VALUE);
          this.cdr.markForCheck();
        },
        (error: any) => {
          console.error('Error fetching allocation limit:', error);
          this.allocationLimit = 100;
          this.cdr.markForCheck();
        }
      );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  confirmSync() {
    if (this.processing) {
      this._snackBar.open("Please wait while we process this request", 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    if (this.syncSaveDtls.length > this.allocationLimit) {
      this._snackBar.open(`Can't select more than ${this.allocationLimit} entities`, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!this.syncSaveDtls || this.syncSaveDtls.length === 0) {
      this._snackBar.open("No entities selected for re-run", 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.processing = true;
    this.loading = true;

    try {
      const returnClobSettingsObj = {
        'tax_year': this.tax_year,
        'scenario': this.scenario,
        'jcd_key': this.jcd_key,
        'sso_id': this.logged_in_user
      };

      const message = "Submitted for Allocation Re-run";
      this.sendDetails(returnClobSettingsObj, this.syncSaveDtls, message);
    } catch (e) {
      this.isSaveClicked = false;
      this.processing = false;
      this.loading = false;
      this._snackBar.open('Error occurred while processing data. Please try again.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      console.error('Error in confirmSync:', e);
      this.cdr.markForCheck();
    }
  }

  private sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    this.allocBatchRerunService.reRunAllocation(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      ecSaveDtls,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.processing = false;
        this.loading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.processing = false;
        this.loading = false;
        this._snackBar.open('Error occurred while processing. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Error in sendDetails:', error);
        this.cdr.markForCheck();
      }
    );
  }
}
